import { useEffect } from "react";

/**
 * Runs `poll` now and then every `intervalMs`, the way the admin console's
 * alert hooks need, without loading the server:
 *  - a tick is skipped while the previous poll is still waiting for the API,
 *    so a slow server does not get a growing queue of the same request;
 *  - nothing is sent while the tab is in the background (document.hidden);
 *    coming back to the tab polls straight away, so alerts are not stale.
 */
export const usePolling = (poll: () => Promise<unknown> | void, intervalMs: number) => {
  useEffect(() => {
    let inFlight = false;
    let stopped = false;

    const tick = async () => {
      if (stopped || inFlight || document.hidden) return;
      inFlight = true;
      try {
        await poll();
      } finally {
        inFlight = false;
      }
    };

    const onVisibility = () => {
      if (!document.hidden) tick();
    };

    tick();
    const timer = window.setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [poll, intervalMs]);
};
